import React, { useState, useEffect } from 'react';
import { Modal, Table, Button, Space, Tag, message, Spin } from 'antd';
import { DownloadOutlined, EyeOutlined, FileTextOutlined, CheckSquareOutlined, FileSearchOutlined } from '@ant-design/icons';
import apiService from '../services/api';
import ContratoAdesaoNOSReviewModal from './ContratoAdesaoNOSReviewModal';

interface DocumentItem {
  id: number;
  lead_id: number;
  user_id: number;
  document_type: string;
  file_path: string;
  field_data: any;
  created_at: string;
  expires_at: string | null;
  is_expired: boolean;
  days_until_expiry: number | null;
  document_source?: 'generated' | 'uploaded';
  file_type?: string;
  original_filename?: string;
  stage?: string;
  field_name?: string;
}

interface LeadDocumentsModalProps {
  open: boolean;
  onClose: () => void;
  leadId: number;
  leadName?: string;
  onRefresh?: () => void;  // Callback to refresh documents
  refreshTrigger?: number;  // Trigger to force refresh
}

const LeadDocumentsModal: React.FC<LeadDocumentsModalProps> = ({
  open,
  onClose,
  leadId,
  leadName = 'Lead',
  onRefresh,
  refreshTrigger = 0
}) => {
  
  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClose();
  };
  
  const handlePreviewClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    setPreviewVisible(false);
    // Clean up blob URL to prevent memory leaks
    if (previewBlobUrl) {
      URL.revokeObjectURL(previewBlobUrl);
      setPreviewBlobUrl(null);
    }
  };

  const [documents, setDocuments] = useState<DocumentItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewData, setPreviewData] = useState<string>('');
  const [previewTitle, setPreviewTitle] = useState<string>('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([]);
  const [downloadingBulk, setDownloadingBulk] = useState(false);
  const [downloadingFiles, setDownloadingFiles] = useState<Set<number>>(new Set());
  const [previewError, setPreviewError] = useState(false);
  const [previewBlobUrl, setPreviewBlobUrl] = useState<string | null>(null);
  const [contratoAdesaoNOSModalVisible, setContratoAdesaoNOSModalVisible] = useState(false);

  // Fetch documents when modal opens or refreshTrigger changes
  useEffect(() => {
    if (open && leadId) {
      fetchDocuments();
    }
  }, [open, leadId, refreshTrigger]);

  // Cleanup blob URL on unmount
  useEffect(() => {
    return () => {
      if (previewBlobUrl) {
        URL.revokeObjectURL(previewBlobUrl);
      }
    };
  }, [previewBlobUrl]);

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      // Fetch both generated documents and uploaded files
      const [documentsResponse, filesResponse] = await Promise.all([
        apiService.getLeadDocuments(leadId),
        apiService.getLeadFiles(leadId)
      ]);
      
      let allDocuments: DocumentItem[] = [];
      
      // Add generated documents
      if (documentsResponse.success && documentsResponse.documents) {
        const generatedDocs: DocumentItem[] = documentsResponse.documents.map((doc: any) => ({
          ...doc,
          document_source: 'generated' as const,
          file_type: 'generated_document'
        }));
        allDocuments = [...generatedDocs];
      }
      
      // Add uploaded files
      if (filesResponse.success && filesResponse.files) {
        const uploadedFiles: DocumentItem[] = filesResponse.files.map((file: any) => ({
          id: file.id,
          lead_id: leadId,
          user_id: file.uploaded_by,
          document_type: file.file_name || file.filename,
          file_path: file.file_path,
          field_data: null,
          created_at: file.uploaded_at,
          expires_at: null,
          is_expired: false,
          days_until_expiry: null,
          document_source: 'uploaded' as const,
          file_type: file.file_type || 'application/octet-stream',
          original_filename: file.file_name,
          stage: file.stage,
          field_name: file.field_name
        }));
        allDocuments = [...allDocuments, ...uploadedFiles];
      }
      
      // Sort by creation date (newest first)
      allDocuments.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      
      setDocuments(allDocuments);
    } catch (error) {
      message.error('Erro ao carregar documentos');
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = async (document: DocumentItem) => {
    try {
      // Open preview modal immediately and show loading state
      setPreviewTitle(document.document_type);
      setPreviewData(''); // Clear previous data
      setPreviewError(false); // Reset error state
      setPreviewBlobUrl(null); // Clear previous blob URL
      setPreviewVisible(true);
      setPreviewLoading(true);
      
      let response;
      
      if (document.document_source === 'generated') {
        // Use previewDocument for generated documents
        response = await apiService.previewDocument(document.id);
      } else {
        // Use previewLeadFile for uploaded files
        response = await apiService.previewLeadFile(document.lead_id, document.id);
      }
      
      // Hide loading state
      setPreviewLoading(false);
      
      if (response.success && 'pdf_data' in response) {
        // Check if file is too large for data URI (limit to 5MB base64)
        const maxDataUriSize = 5 * 1024 * 1024; // 5MB
        if (response.pdf_data.length > maxDataUriSize) {
          // Create blob URL for large files
          const binaryString = atob(response.pdf_data);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          const blob = new Blob([bytes], { type: 'application/pdf' });
          const blobUrl = URL.createObjectURL(blob);
          setPreviewBlobUrl(blobUrl);
          setPreviewData(''); // Don't set data for large files
        } else {
          setPreviewData(response.pdf_data);
          setPreviewBlobUrl(null);
        }
      } else if (!response.success && 'error' in response) {
        // Show the specific error message from the backend
        setPreviewVisible(false); // Close modal on error
        message.error(response.error);
      } else {
        // Try to handle response even if structure is unexpected
        if (response && typeof response === 'object') {
          // Check if response has any data that might be PDF data
          const possibleDataKeys = ['data', 'pdf_data', 'content', 'file_data'];
          for (const key of possibleDataKeys) {
            if ((response as any)[key] && typeof (response as any)[key] === 'string') {
              setPreviewData((response as any)[key]);
              break;
            }
          }
        }
        
        if (!previewData) {
          setPreviewVisible(false); // Close modal on error
          message.error('Erro ao carregar pré-visualização');
        }
      }
    } catch (error) {
      setPreviewLoading(false);
      setPreviewVisible(false); // Close modal on error
      message.error('Erro ao carregar pré-visualização');
    }
  };

  const handleDownload = async (document: DocumentItem) => {
    // Add document ID to downloading set
    setDownloadingFiles(prev => new Set(prev).add(document.id));
    
    try {
      let response;
      
      if (document.document_source === 'generated') {
        // Use downloadDocument for generated documents
        response = await apiService.downloadDocument(document.id);
      } else {
        // Use downloadLeadFile for uploaded files
        response = await apiService.downloadLeadFile(document.lead_id, document.id);
      }
      
      if (response.success) {
        // Create a blob and download
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = window.document.createElement('a') as HTMLAnchorElement;
        link.href = url;
        link.download = `${document.document_type}_${leadName}.pdf`;
        window.document.body.appendChild(link);
        link.click();
        window.document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        message.success('Documento descarregado com sucesso');
      } else {
        message.error('Erro ao descarregar documento');
      }
    } catch (error) {
      message.error('Erro ao descarregar documento');
    } finally {
      // Remove document ID from downloading set
      setDownloadingFiles(prev => {
        const newSet = new Set(prev);
        newSet.delete(document.id);
        return newSet;
      });
    }
  };

  const handleBulkDownload = async () => {
    setDownloadingBulk(true);
    try {
      const selectedDocs = documents.filter(doc => selectedRowKeys.includes(doc.id));
      
      // Add all selected document IDs to downloading set
      setDownloadingFiles(prev => {
        const newSet = new Set(prev);
        selectedDocs.forEach(doc => newSet.add(doc.id));
        return newSet;
      });
      
      for (const doc of selectedDocs) {
        try {
          let response;
          
          if (doc.document_source === 'generated') {
            response = await apiService.downloadDocument(doc.id);
          } else {
            response = await apiService.downloadLeadFile(doc.lead_id, doc.id);
          }
          
          if (response.success) {
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const link = window.document.createElement('a') as HTMLAnchorElement;
            link.href = url;
            link.download = `${doc.document_type}_${leadName}.pdf`;
            window.document.body.appendChild(link);
            link.click();
            window.document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
          }
        } catch (error) {
        }
      }
      
      message.success(`${selectedDocs.length} documentos descarregados com sucesso`);
      setSelectionMode(false);
      setSelectedRowKeys([]);
    } catch (e) {
      message.error('Erro ao descarregar documentos selecionados');
    } finally {
      setDownloadingBulk(false);
      // Clear all downloading states
      setDownloadingFiles(new Set());
    }
  };

  const getExpiryTag = (document: DocumentItem) => {
    // Uploaded files don't have expiry
    if (document.document_source === 'uploaded') {
      return <Tag color="blue">Carregado</Tag>;
    }
    
    // Generated documents have expiry
    if (document.is_expired) {
      return <Tag color="red">Expirado</Tag>;
    } else if (document.days_until_expiry !== null && document.days_until_expiry <= 1) {
      return <Tag color="orange">Expira hoje</Tag>;
    } else if (document.days_until_expiry !== null && document.days_until_expiry <= 3) {
      return <Tag color="orange">Expira em {document.days_until_expiry} dias</Tag>;
    } else if (document.days_until_expiry !== null) {
      return <Tag color="green">Expira em {document.days_until_expiry} dias</Tag>;
    } else {
      return <Tag color="green">Válido</Tag>;
    }
  };

  const columns = [
    {
      title: 'Tipo de Documento',
      dataIndex: 'document_type',
      key: 'document_type',
      render: (text: string, record: DocumentItem) => (
        <Space>
          <FileTextOutlined />
          <div>
            <div>{text}</div>
            {record.document_source && (
              <div style={{ fontSize: '12px', color: '#666' }}>
                {record.document_source === 'generated' ? 'Gerado' : 'Carregado'}
                {record.stage && ` • ${record.stage}`}
              </div>
            )}
          </div>
        </Space>
      ),
    },
    {
      title: 'Data de Criação',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => new Date(date).toLocaleDateString('pt-PT'),
    },
    {
      title: 'Estado',
      key: 'status',
      render: (record: DocumentItem) => getExpiryTag(record),
    },
    {
      title: 'Ações',
      key: 'actions',
      render: (record: DocumentItem) => {
        const isDownloading = downloadingFiles.has(record.id);
        return (
          <Space>
            <Button
              type="link"
              icon={<EyeOutlined />}
              onClick={() => handlePreview(record)}
              size="small"
            >
              Pré-visualizar
            </Button>
            <Button
              type="link"
              icon={<DownloadOutlined />}
              onClick={() => handleDownload(record)}
              size="small"
              loading={isDownloading}
              disabled={isDownloading}
            >
              {isDownloading ? 'A descarregar...' : 'Descarregar'}
            </Button>
          </Space>
        );
      },
    },
  ];

  return (
    <>
      <Modal
        title={`Documentos - ${leadName}`}
        open={open}
        onCancel={handleClose}
        zIndex={999999}
        maskClosable={false}
        destroyOnClose={true}
        footer={[
          <Button key="refresh" onClick={fetchDocuments} loading={loading}>
            Atualizar
          </Button>,
          selectionMode ? (
            <Button
              key="download-selected"
              type="primary"
              icon={<DownloadOutlined />}
              onClick={handleBulkDownload}
              disabled={selectedRowKeys.length === 0}
              loading={downloadingBulk}
            >
              {downloadingBulk ? 'A descarregar...' : 'Download Selecionados'}
            </Button>
          ) : null,
          <Button
            key="select-mode"
            icon={<CheckSquareOutlined />}
            type={selectionMode ? 'default' : 'primary'}
            onClick={() => setSelectionMode(m => !m)}
            style={{ marginLeft: 8 }}
          >
            {selectionMode ? 'Cancelar Seleção' : 'Selecionar Documentos'}
          </Button>,
          <Button
            key="contrato-adesao-nos"
            icon={<FileSearchOutlined />}
            type="default"
            onClick={() => setContratoAdesaoNOSModalVisible(true)}
            style={{ marginLeft: 8 }}
          >
            Contrato de Adesão NOS
          </Button>,
          <Button key="close" onClick={onClose}>
            Fechar
          </Button>,
        ]}
        width={800}
      >
        {loading ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Spin size="large" />
            <p>A carregar documentos...</p>
          </div>
        ) : documents.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <FileTextOutlined style={{ fontSize: '48px', color: '#ccc' }} />
            <p>Nenhum documento associado a este lead</p>
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={documents}
            rowKey="id"
            pagination={false}
            size="small"
            rowSelection={selectionMode ? {
              selectedRowKeys,
              onChange: (keys: React.Key[]) => setSelectedRowKeys(keys as number[]),
            } : undefined}
          />
        )}
      </Modal>

      {/* PDF Preview Modal */}
      <Modal
        title={`Pré-visualização - ${previewTitle}`}
        open={previewVisible}
        onCancel={handlePreviewClose}
        footer={[
          <Button key="close" onClick={handlePreviewClose}>
            Fechar
          </Button>,
        ]}
        width={800}
        zIndex={999998}
        maskClosable={false}
        destroyOnClose={true}
        style={{ top: 20 }}
      >
        {previewLoading ? (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '600px',
            flexDirection: 'column',
            gap: '16px'
          }}>
            <Spin size="large" />
            <div>A carregar pré-visualização...</div>
          </div>
        ) : previewData ? (
          <div>
            {!previewError ? (
              <iframe
                src={previewBlobUrl || `data:application/pdf;base64,${previewData}`}
                width="100%"
                height="600px"
                style={{ border: 'none' }}
                onLoad={() => {
                  setPreviewError(false);
                }}
                onError={(e) => {
                  setPreviewError(true);
                }}
                title={`Pré-visualização - ${previewTitle}`}
              />
            ) : (
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column',
                justifyContent: 'center', 
                alignItems: 'center', 
                height: '600px',
                border: '2px dashed #d9d9d9',
                borderRadius: '6px',
                padding: '20px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '16px', color: '#666', marginBottom: '16px' }}>
                  Não foi possível carregar a pré-visualização
                </div>
                <div style={{ fontSize: '14px', color: '#999', marginBottom: '20px' }}>
                  O ficheiro pode estar corrompido ou num formato não suportado
                </div>
                <Button 
                  type="primary"
                  onClick={() => {
                    // Use existing blob URL if available, otherwise create new one
                    if (previewBlobUrl) {
                      const link = document.createElement('a');
                      link.href = previewBlobUrl;
                      link.download = `${previewTitle}.pdf`;
                      link.click();
                    } else {
                      // Create blob and download as fallback
                      const blob = new Blob([Uint8Array.from(atob(previewData), c => c.charCodeAt(0))], { type: 'application/pdf' });
                      const url = URL.createObjectURL(blob);
                      const link = document.createElement('a');
                      link.href = url;
                      link.download = `${previewTitle}.pdf`;
                      link.click();
                      URL.revokeObjectURL(url);
                    }
                  }}
                >
                  Descarregar PDF para verificar
                </Button>
              </div>
            )}
            <div style={{ marginTop: '10px', textAlign: 'center' }}>
              <Button 
                type="link" 
                onClick={() => {
                  // Use existing blob URL if available, otherwise create new one
                  if (previewBlobUrl) {
                    const link = document.createElement('a');
                    link.href = previewBlobUrl;
                    link.download = `${previewTitle}.pdf`;
                    link.click();
                  } else {
                    // Create blob and download as fallback
                    const blob = new Blob([Uint8Array.from(atob(previewData), c => c.charCodeAt(0))], { type: 'application/pdf' });
                    const url = URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = `${previewTitle}.pdf`;
                    link.click();
                    URL.revokeObjectURL(url);
                  }
                }}
              >
                Descarregar PDF para verificar
              </Button>
            </div>
          </div>
        ) : (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '600px',
            color: '#999'
          }}>
            Nenhum documento para pré-visualizar
          </div>
        )}
      </Modal>
      
      <ContratoAdesaoNOSReviewModal
        open={contratoAdesaoNOSModalVisible}
        onClose={() => setContratoAdesaoNOSModalVisible(false)}
      />
    </>
  );
};

export default LeadDocumentsModal;